<template>
    <div class="f fv pr ath-form-timepicker">
        <div class="f fh fa-c dw-10 sm-b-xs" v-if="lbl">
            <slot name="labelLeft"></slot>
            <span class="fg ath-form-timepicker-label">{{ uiTitle }}<slot name="labelAfter"></slot></span>
            <slot name="labelRight"></slot>
        </div>
        <div class="f fh fa-c dw-10">
            <div class="fg pr ath-form-timepicker-el"
                :class="[disabled ? 'el--disabled' : '', !uiLabels ? 'el--nolabels' : '']"
                data-placeholder="HH">
                <input type="text"
                    class="fg dw-10"
                    maxlength="2"
                    @blur="blurHandler($event, 'hour')"
                    @keydown="keyHandler($event, 'hour')"
                    v-model="hour"
                    :disabled="disabled" />
            </div>
            <template v-if="uiMinutesVisible">
                <span class="fns sp-l-xxs sp-r-xxs">:</span>
                <div class="fg pr ath-form-timepicker-el"
                    :class="[disabled ? 'el--disabled' : '', !uiLabels ? 'el--nolabels' : '']"
                    data-placeholder="MM">
                    <input type="text"
                        class="fg dw-10"
                        maxlength="2"
                        @blur="blurHandler($event, 'minute')"
                        @keydown="keyHandler($event, 'minute')"
                        v-model="minute"
                        :disabled="disabled" />
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { isNumberBetween, join } from '@aretheyhappy/utils';

const WHITE_LISTED_KEYCODES = [
    8,   //  Backspace
    9,   //  Tab
    46,  //  Delete
    38,  //  Arrow up
    37,  //  Arrow left
    39,  //  Arrow right
    40,  //  Arrow down
    48,  //  Digits: 0 standard
    96,  //  Digits: 0 numpad
    49,  //  Digits: 1 standard
    97,  //  Digits: 1 numpad
    50,  //  Digits: 2 standard
    98,  //  Digits: 2 numpad
    51,  //  Digits: 3 standard
    99,  //  Digits: 3 numpad
    52,  //  Digits: 4 standard
    100, //  Digits: 4 numpad
    53,  //  Digits: 5 standard
    101, //  Digits: 5 numpad
    54,  //  Digits: 6 standard
    102, //  Digits: 6 numpad
    55,  //  Digits: 7 standard
    103, //  Digits: 7 numpad
    56,  //  Digits: 8 standard
    104, //  Digits: 8 numpad
    57,  //  Digits: 9 standard
    105, //  Digits: 9 numpad
];

//  Convert data for use in the ui
const deserialize = val => `${parseInt(val) < 10 ? '0' : ''}${val}`;

export default {
    name: 'mead-form-timepicker',
    props: {
        /**
         * Label i18n key rendered above the time inputs. Only shown when truthy.
         */
        lbl             : {type: [String, Boolean], default: () => false},
        /**
         * Entity name interpolated into the label when translating it.
         */
        lblEntity       : {type: [Boolean, String], default: () => false},
        /**
         * Whether the interpolated entity name should be lowercased in the translated label.
         */
        lblEntityLc     : {type: Boolean, default: () => true},
        /**
         * Currently selected time, as `{hour, minute}` (v-model).
         */
        value           : {type: Object, default: () => ({})},
        /**
         * Disables the hour/minute inputs.
         */
        disabled        : {type: Boolean, default: () => false},
        /**
         * Amount the hour changes by on each arrow-key increment/decrement.
         */
        uiHourStep      : {type: Number, default: () => 1},
        /**
         * Default hour used when `value.hour` isn't a valid hour.
         */
        uiHourDefault   : {type: Number, default: () => 8},
        /**
         * Minimum allowed hour value; decrementing below this wraps around to `uiHourMax`.
         */
        uiHourMin       : {type: Number, default: () => 0},
        /**
         * Maximum allowed hour value; incrementing above this wraps around to `uiHourMin`.
         */
        uiHourMax       : {type: Number, default: () => 23},
        /**
         * Amount the minute changes by on each arrow-key increment/decrement.
         */
        uiMinuteStep    : {type: Number, default: () => 5},
        /**
         * Default minute used when `value.minute` isn't a valid minute.
         */
        uiMinuteDefault : {type: Number, default: () => 0},
        /**
         * Minimum allowed minute value; decrementing below this wraps around to `uiMinuteMax` (and
         * decrements the hour).
         */
        uiMinuteMin     : {type: Number, default: () => 0},
        /**
         * Maximum allowed minute value; incrementing above this wraps around to `uiMinuteMin` (and
         * increments the hour).
         */
        uiMinuteMax     : {type: Number, default: () => 59},
        /**
         * Whether to show the "HH"/"MM" placeholder labels beneath each input.
         */
        uiLabels        : {type: Boolean, default: () => true},
        /**
         * Whether the minute input is shown alongside the hour input.
         */
        uiMinutesVisible: {type: Boolean, default: () => false},
        /**
         * Appends an "(optional)" suffix to the label.
         */
        uiOptional      : {type: Boolean, default: () => false},
    },
    data () {
        return {
            hour    : deserialize(this.uiHourDefault),
            minute  : deserialize(this.uiMinuteDefault),
        };
    },
    methods: {
        init: function () {
            //  Get hour, if not available use default
            this.update(
                'hour',
                0,
                isNumberBetween(this.value.hour, 0, 23) ? parseInt(this.value.hour) : this.uiHourDefault
            );

            //  Get minute, if not available use default
            this.update(
                'minute',
                0,
                isNumberBetween(this.value.minute, 0, 59) ? parseInt(this.value.minute) : this.uiMinuteDefault
            );
        },
        //  Notify updated model to parent component
        notify: function () {
            this.$emit('input', {hour: this.hour, minute: this.minute});
        },
        //  Update internal data props for hour/minute based on change in input
        update: function (prop, update_by, override = false) {
            let new_val = override !== false ? parseInt(override, 10) : parseInt(this[prop], 10) + update_by;

            switch (prop) {
                case 'hour':
                    if (new_val > this.uiHourMax) {
                        new_val = this.uiHourMin + (new_val % this.uiHourMax) - 1;
                    } else if (new_val < this.uiHourMin) {
                        new_val = this.uiHourMax - Math.abs(new_val) + 1;
                    }
                    break;
                case 'minute':
                    if (new_val > this.uiMinuteMax) {
                        new_val = this.uiMinuteMin + (new_val % this.uiMinuteMax) - 1;
                    } else if (new_val < this.uiMinuteMin) {
                        new_val = this.uiMinuteMax - Math.abs(new_val) + 1;
                    }
                    break;
                default:
                    break;
            }

            this[prop] = deserialize(new_val);

            this.notify();
            return new_val;
        },
        //  Increment hour/minute
        increment: function (prop) {
            if (prop === 'hour') {
                this.update(prop, this.uiHourStep);
            } else {
                const old = this.minute;

                this.update(prop, this.uiMinuteStep);

                //  If it went over 59, increment hour
                if (this.minute <= old) this.increment('hour');
            }
        },
        //  Decrement hour/minute
        decrement: function (prop) {
            if (prop === 'hour') {
                this.update(prop, -this.uiHourStep);
            } else {
                const old = this.minute;

                this.update(prop, -this.uiMinuteStep);

                //  If it went under 0, decrement hour
                if (this.minute >= old) this.decrement('hour');
            }
        },
        //  Handle keyboard entries
        keyHandler: function (evt, prop) {
            if (!WHITE_LISTED_KEYCODES.includes(evt.keyCode)) {
                evt.preventDefault();
                return false;
            }

            //  Execute special cases for increment (arrow up) or
            //  decrement (arrow down), all the rest is default behaviour
            switch (evt.keyCode) {
                case 38:
                    this.increment(prop);
                    break;
                case 40:
                    this.decrement(prop);
                    break;
                default:
                    break;
            }
        },
        //  Handle blur, properly call update on prop
        blurHandler: function (evt, prop) {
            this.update(prop, 0);
        },
    },
    computed: {
        uiTitle: function () {
            return join([
                this.lblEntity
                    ? MeadCommon.I18N[this.lblEntityLc ? 'actionLc' : 'action'](this.lbl, this.lblEntity)
                    : MeadCommon.I18N.go(this.lbl),
                this.uiOptional
                    ? `(${MeadCommon.I18N.go('mead.labels.optional')})`
                    : false,
            ], {delim: ' '});
        },
    },
    created () {
        this.init();
    },
};
</script>

<style lang="scss" scoped>
@import '../../../../sass/_variables.scss';

input {
    border-radius: $br-m;
    border: none;
    box-shadow: inset 0 0 4rem 4rem var(--c-brand-light-bg);
    background-color: var(--c-brand-light-bg);
    color: var(--c-brand-light-fg);
    height: 4rem;
    line-height: 1;
    font-size: $fs-s;
    color: var(--c-txt);

    &:active,
    &:focus {
        outline: none;
    }

    &[disabled="disabled"] {
        opacity: .65;
        cursor: not-allowed;
    }
}

.ath-form-timepicker-label {
    font-family: $ff-b;
    font-weight: 400;
    line-height: $lh-xs;
    font-size: $fs-s;
    color: var(--c-txt);
}

.ath-form-timepicker-el {
    &:not(.el--nolabels) {
        min-width: 6rem;

        &:after {
            content: attr(data-placeholder);
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            display: inline-block;
            padding: 0 $s-xs 0 $s-s;
            font-size: $fs-xxs;
            font-weight: 700;
            line-height: 4.2rem;
            color: var(--c-txtt);
            pointer-events: none;
        }

        input {
            padding: 0 $s-s 0 $s-xl;
        }
    }

    &.el--nolabels {
        min-width: 4rem;

        input {
            padding: 0 $s-s;
        }
    }
}

//  SIZES

.ath-form-timepicker.el--s-s {
    .ath-form-timepicker-el {
        input {
            height: 3rem;
            padding: 0 $s-xs 0 $s-xl;
        }

        &:not(.el--nolabels):after {
            padding: 0 $s-xs;
        }

        &.el--nolabels {
            min-width: 3rem;

            input {
                text-align: center;
                padding: 0 $s-xs;
            }
        }
    }
}
</style>
